import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Facebook, Instagram, Send, Twitter, Linkedin } from 'lucide-react';
import { companyInfo, socialLinks } from '../mock/data';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About Us', path: '/about' },
    { name: 'Market Insights', path: '/insights' },
    { name: 'Contact', path: '/contact' },
    { name: 'Complaint Board', path: '/complaint-board' }
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms and Conditions', path: '/terms' },
    { name: 'Disclaimer', path: '/disclaimer' },
    { name: 'Refund Policy', path: '/refund-policy' },
    { name: 'MITC', path: '/mitc' },
    { name: 'Investor Charter', path: '/investor-charter' },
    { name: 'Accessibility', path: '/accessibility' }
  ];

  return (
    <footer className="bg-gradient-to-b from-slate-950 to-slate-900 border-t border-amber-500/20" role="contentinfo">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h2 className="text-xl font-bold text-amber-400 mb-4">
              {companyInfo.name}
            </h2>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              {companyInfo.tagline}
            </p>
            <div className="space-y-2">
              <p className="text-xs text-gray-400">SEBI Reg: {companyInfo.sebiReg}</p>
              <p className="text-xs text-gray-400">GST: {companyInfo.gstNo}</p>
            </div>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick links">
            <h2 className="text-lg font-semibold text-amber-400 mb-4">Quick Links</h2>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-amber-400 text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Legal links">
            <h2 className="text-lg font-semibold text-amber-400 mb-4">Legal</h2>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-amber-400 text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info */}
          <div>
            <h2 className="text-lg font-semibold text-amber-400 mb-4">Contact Us</h2>
            <div className="space-y-3">
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-2 text-gray-400 hover:text-amber-400 text-sm transition-colors duration-200"
                aria-label={`Send email to ${companyInfo.email}`}
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                {companyInfo.email}
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-2 text-gray-400 hover:text-amber-400 text-sm transition-colors duration-200"
                aria-label={`Call ${companyInfo.phone}`}
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                {companyInfo.phone}
              </a>
            </div>

            {/* Social Links */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-300 mb-3">Follow Us</h3>
              <div className="flex gap-3">
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-900/20 hover:bg-emerald-700/30 rounded-lg transition-all duration-200 group"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook className="w-5 h-5 text-gray-400 group-hover:text-amber-400" aria-hidden="true" />
                </a>
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-900/20 hover:bg-emerald-700/30 rounded-lg transition-all duration-200 group"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5 text-gray-400 group-hover:text-amber-400" aria-hidden="true" />
                </a>
                <a
                  href={socialLinks.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-900/20 hover:bg-emerald-700/30 rounded-lg transition-all duration-200 group"
                  aria-label="Join us on Telegram"
                >
                  <Send className="w-5 h-5 text-gray-400 group-hover:text-amber-400" aria-hidden="true" />
                </a>
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-900/20 hover:bg-emerald-700/30 rounded-lg transition-all duration-200 group"
                  aria-label="Follow us on Twitter"
                >
                  <Twitter className="w-5 h-5 text-gray-400 group-hover:text-amber-400" aria-hidden="true" />
                </a>
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-900/20 hover:bg-emerald-700/30 rounded-lg transition-all duration-200 group"
                  aria-label="Connect with us on LinkedIn"
                >
                  <Linkedin className="w-5 h-5 text-gray-400 group-hover:text-amber-400" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-amber-500/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} | {companyInfo.name} | All rights reserved
            </p>
            <p className="text-gray-400 text-xs text-center max-w-2xl">
              INVESTMENTS IN THE SECURITIES MARKET ARE SUBJECT TO MARKET RISK. PLEASE READ ALL RELATED DOCUMENTS CAREFULLY BEFORE INVESTING.
            </p>
          </div>
          <div className="mt-4 text-center">
            <p className="text-gray-400 text-xs">
              If you experience accessibility issues on this website, please contact{' '}
              <a href="mailto:care@equipulseresearch.com" className="text-amber-400 hover:text-amber-300 underline">
                care@equipulseresearch.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
